import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { selectError } from '../../../../@store/products/ca-numbers/ca-numbers.selectors';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  selectExportDetails,
  selectExportDetailsError,
  selectExportDetailsLoading,
  selectExportOrderError,
  selectExportOrderLoading,
} from '../../../../@store/orders/orders.selectors';
import { exportOrder, loadExportDetails } from '../../../../@store/orders/orders.actions';
import { OrderExportDetails, singleOrderDetails } from '../../../../@store/orders/orders.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { pairwise, withLatestFrom, filter } from 'rxjs/operators';

@Component({
  selector: 'ciwc-order-export',
  imports: [
    CommonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButton,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatCheckboxModule,
  ],
  standalone: true,
  templateUrl: './order-export.component.html',
  styleUrl: './order-export.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderExportComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly dialogRef = inject(MatDialogRef<OrderExportComponent>);
  private readonly data: { order: singleOrderDetails } = inject(MAT_DIALOG_DATA);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroy$ = new Subject<void>();

  form: FormGroup = this.fb.group({
    shopTraveller: this.fb.array([]),
    attachments: this.fb.array([]),
    shipmentAttachments: this.fb.array([]),
    jobAttachments: this.fb.array([]),
  });

  error = this.store.selectSignal(selectError);
  submitted = false;

  exportDetails$ = this.store.select(selectExportDetails);
  exportDetailsLoading$ = this.store.select(selectExportDetailsLoading);
  exportDetailsError$ = this.store.select(selectExportDetailsError);
  exportOrderLoading$ = this.store.select(selectExportOrderLoading);
  exportOrderLoading = toSignal(this.exportOrderLoading$, { initialValue: false });
  exportOrderError$ = this.store.select(selectExportOrderError);

  ngOnInit(): void {
    this.store.dispatch(loadExportDetails({ orderId: this.data.order.id }));
    this.exportDetails$.pipe(takeUntil(this.destroy$)).subscribe((details) => {
      if (details) {
        this.setExportDetailsForm(details);
      }
    });

    // Listen for exportOrder success and close the dialog
    this.exportOrderLoading$
      .pipe(
        pairwise(),
        withLatestFrom(this.exportOrderError$),
        filter(([[prev, curr], error]) => prev && !curr && !error),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.dialogRef.close(true);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setExportDetailsForm(details: OrderExportDetails) {
    // Remove previous arrays if they exist
    ['shopTraveller', 'attachments', 'jobAttachments', 'shipmentAttachments'].forEach((key) => {
      if (this.form.contains(key)) this.form.removeControl(key);
    });

    if (details.shopTraveller && details.shopTraveller.length > 0) {
      this.form.addControl(
        'shopTraveller',
        this.fb.array(details.shopTraveller.map(() => this.fb.control(false))),
      );
    }
    if (details.attachments && details.attachments.length > 0) {
      this.form.addControl(
        'attachments',
        this.fb.array(details.attachments.map(() => this.fb.control(false))),
      );
    }
    if (details.jobAttachments && details.jobAttachments.length > 0) {
      this.form.addControl(
        'jobAttachments',
        this.fb.array(details.jobAttachments.map(() => this.fb.control(false))),
      );
    }
    if (details.shipmentAttachments && details.shipmentAttachments.length > 0) {
      this.form.addControl(
        'shipmentAttachments',
        this.fb.array(details.shipmentAttachments.map(() => this.fb.control(false))),
      );
    }
  }

  hasShopTraveller(details: OrderExportDetails): boolean {
    return !!details.shopTraveller && details.shopTraveller.length > 0;
  }
  hasAttachments(details: OrderExportDetails): boolean {
    return !!details.attachments && details.attachments.length > 0;
  }
  hasJobAttachments(details: OrderExportDetails): boolean {
    return !!details.jobAttachments && details.jobAttachments.length > 0;
  }
  hasShipmentAttachments(details: OrderExportDetails): boolean {
    return !!details.shipmentAttachments && details.shipmentAttachments.length > 0;
  }

  get shopTravellerArray(): FormArray {
    return this.form.get('shopTraveller') as FormArray;
  }
  get attachmentsArray(): FormArray {
    return this.form.get('attachments') as FormArray;
  }

  getShopTravellerControl(index: number) {
    return this.shopTravellerArray.at(index) as FormControl;
  }
  getAttachmentsControl(index: number) {
    return this.attachmentsArray.at(index) as FormControl;
  }

  get jobAttachmentsArray(): FormArray {
    return this.form.get('jobAttachments') as FormArray;
  }
  get shipmentAttachmentsArray(): FormArray {
    return this.form.get('shipmentAttachments') as FormArray;
  }

  getJobAttachmentsControl(index: number) {
    return this.jobAttachmentsArray?.at(index) as FormControl;
  }
  getShipmentAttachmentsControl(index: number) {
    return this.shipmentAttachmentsArray?.at(index) as FormControl;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.submitted = true;
    if (this.form.valid && !this.exportOrderLoading()) {
      const details = this.form.value;
      const buildPayload = (category: string, ids: any[], selected: boolean[]) =>
        ids.map((item: any, idx: number) => ({ id: item.id, selected: selected[idx] }));

      let shopTraveller: any[] = [];
      let attachments: any[] = [];
      let jobAttachments: any[] = [];
      let shipmentAttachments: any[] = [];
      this.exportDetails$.pipe(takeUntil(this.destroy$)).subscribe((apiDetails) => {
        if (apiDetails) {
          if (apiDetails.shopTraveller) {
            shopTraveller = apiDetails.shopTraveller.map((item: any, idx: number) => ({
              id: item.id,
              selected: this.shopTravellerArray?.at(idx)?.value || false,
            }));
          }
          if (apiDetails.attachments) {
            attachments = apiDetails.attachments.map((item: any, idx: number) => ({
              id: item.id,
              selected: this.attachmentsArray?.at(idx)?.value || false,
            }));
          }
          if (apiDetails.jobAttachments) {
            jobAttachments = apiDetails.jobAttachments.map((item: any, idx: number) => ({
              id: item.id,
              selected: this.jobAttachmentsArray?.at(idx)?.value || false,
            }));
          }
          if (apiDetails.shipmentAttachments) {
            shipmentAttachments = apiDetails.shipmentAttachments.map((item: any, idx: number) => ({
              id: item.id,
              selected: this.shipmentAttachmentsArray?.at(idx)?.value || false,
            }));
          }
        }
      });
      const payload = {
        shopTraveller,
        attachments,
        jobAttachments,
        shipmentAttachments,
      };
      this.store.dispatch(exportOrder({ orderId: this.data.order.id, payload }));
    }
  }

  getErrorMessage(): string | null {
    const err = this.error();
    if (!this.submitted || !err) return null;
    if (typeof err === 'string') return err;
    if (err.error && typeof err.error === 'string') return err.error;
    if (err.error && err.error.errors) return err.error.errors;

    return 'An unknown error occurred.';
  }
}
